import { cn } from '@april9au/react-common/utils';

import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';

/**
 * Course progress as `total` labelled segments — the journey, module-complete
 * and passport screens' header slot.
 *
 * Segments are navy when done, yellow for the one the learner is on and grey
 * beyond it, which is the same "completed / current / to do" language the
 * journey cards use. `labels` name the modules in order; they are the
 * accessible name of each segment and are shown under it from `sm` up, where
 * three or four of them fit without truncating into nonsense.
 *
 * Given `segments`, every lamp becomes a gauge: each one reports its own
 * module — solid navy when finished, a yellow fill over a pale yellow track for
 * one part-played, and an empty grey track for one not opened yet. The module
 * the learner is on keeps its pale yellow track even at zero fill, so "you are
 * here" survives. It is the same anatomy the journey cards use, so a module's
 * progress reads the same in the header as it does on its card.
 *
 * Without `segments` each one is a plain lamp keyed off `done`, which is what
 * the module-complete and passport screens want — they arrive with everything
 * finished and know nothing about a run in progress.
 *
 * Every string here carries `dir="auto"`. The `language` documents do not
 * exist in production yet, so in Arabic these read as English fallbacks inside
 * an RTL paragraph — and the bidi algorithm then renders `0 of 4` as `of 4 0`.
 * `auto` resolves the direction from the text itself, so a fallback reads LTR
 * and a real translation reads RTL, with no per-language special case.
 *
 * `total` is required and always comes from `validTopicsFor` (or its client
 * twin `journeyRowsFor`), never from a constant: the course is four modules for
 * adults and children but **three** for teens, so a hardcoded 4 tells a
 * teenager they are 3 of 4 done when they have finished everything.
 */
export function OverallProgress({
  done,
  total,
  labels = [],
  segments: modules,
  className,
}: {
  /** How many modules have a score. Clamped into `0…total`. */
  done: number;
  /** How many modules this profile has — `validTopicsFor(...).length`. */
  total: number;
  /** Module titles in journey order. Shorter or longer than `total` is fine. */
  labels?: Array<string>;
  /**
   * Per-module state, index-aligned with `labels`. Omit for plain lamps.
   *
   * `done` comes from the module's own score rather than from a count, so a
   * learner who finishes module 3 before module 1 lights the segment they
   * actually finished.
   */
  segments?: ReadonlyArray<{ done: boolean; fraction: number }>;
  className?: string;
}) {
  const { header } = useStrings();

  const completed = Math.max(0, Math.min(done, total));
  const segments = Array.from({ length: total }, (_, index) => index);
  const count = formatString(header.header_progress_count, { done: completed, total });
  // The module the learner is on: the first they have not finished, the same
  // rule `deriveJourney` uses to pick the recommended one.
  const currentIndex = modules ? modules.findIndex((module) => !module.done) : completed;

  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <p dir="auto" className="text-muted-foreground text-[0.6875rem] font-bold tracking-wide uppercase">
          {header.header_progress_label}
        </p>
        <p dir="auto" className="text-surf-dark-blue text-[0.6875rem] font-bold tabular-nums">
          {count}
        </p>
      </div>

      <ol
        className="mt-1.5 flex gap-1.5"
        aria-label={`${header.header_progress_label}: ${count}`}
        // The segments are decorative detail on top of the label above, which
        // already states the count — announcing four list items adds nothing.
        role="list"
      >
        {segments.map((index) => {
          const module = modules?.[index];
          const isDone = module ? module.done : index < completed;
          const isCurrent = index === currentIndex;
          const label = labels[index];

          return (
            <li key={index} className="min-w-0 flex-1">
              {modules && !isDone ? (
                <span
                  className={cn(
                    'relative block h-1.5 overflow-hidden rounded-full',
                    // Pale yellow is "you are here" and survives a zero fill;
                    // grey is a module the learner has not reached.
                    isCurrent ? 'bg-surf-yellow/30' : 'bg-surf-gray',
                  )}
                >
                  <span
                    className="bg-surf-yellow absolute inset-0 origin-left rounded-full transition-transform duration-500 ease-out motion-reduce:transition-none rtl:origin-right"
                    style={{ transform: `scaleX(${Math.max(0, Math.min(module?.fraction ?? 0, 1))})` }}
                  />
                </span>
              ) : (
                <span
                  className={cn(
                    'block h-1.5 rounded-full',
                    isDone ? 'bg-surf-dark-blue' : isCurrent ? 'bg-surf-yellow' : 'bg-surf-gray',
                  )}
                />
              )}
              {label ? (
                <span
                  dir="auto"
                  className={cn(
                    'mt-1 hidden truncate text-[0.6875rem] leading-tight sm:block',
                    isDone || isCurrent ? 'text-surf-dark-blue font-semibold' : 'text-muted-foreground',
                  )}
                  title={label}
                >
                  {label}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
